import { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  Pressable,
  Modal,
  Platform,
  ScrollView,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { Calendar } from "lucide-react-native";
import {
  generateCalendarDays,
  getToday,
  WEEKDAY_LABELS,
} from "@src/packages/utils/date";
import { colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const MONTH_ABBREVS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function generateYearRange(centerYear: number): number[] {
  const years: number[] = [];
  for (let i = centerYear - 6; i <= centerYear + 6; i++) {
    years.push(i);
  }
  return years;
}

export function MoonDatePicker({
  value,
  onChange,
  placeholder = "Date",
  textStyle,
  iconSize = 11,
  containerStyle,
}: {
  value: string; // YYYY-MM-DD or ""
  onChange: (date: string) => void;
  placeholder?: string;
  textStyle?: StyleProp<TextStyle>;
  iconSize?: number;
  /** Override the outer Pressable styles. Use this when the trigger needs to
   *  fill its parent width and left-align content (e.g., inside a form input). */
  containerStyle?: StyleProp<ViewStyle>;
}) {
  const capturePress = usePressCapture("MoonDatePicker");
  const [open, setOpen] = useState(false);
  const today = useMemo(() => getToday(), []);
  const parsed = useMemo(() => {
    if (!value) return null;
    const d = new Date(value + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  }, [value]);

  const [viewYear, setViewYear] = useState(
    () => parsed?.getFullYear() ?? today.getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    () => parsed?.getMonth() ?? today.getMonth(),
  );

  const yearRange = useMemo(() => generateYearRange(viewYear), [viewYear]);

  useEffect(() => {
    if (open && parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [open, parsed]);

  const days = useMemo(
    () => generateCalendarDays(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const selectDay = (day: number) => {
    const mm = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    onChange(`${viewYear}-${mm}-${dd}`);
    setOpen(false);
  };

  const display = parsed
    ? `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`
    : null;

  return (
    <>
      <Pressable
        onPress={() => {
          capturePress(pressLabel(display, placeholder), "open_date_picker");
          setOpen(true);
        }}
        style={[
          {
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            paddingHorizontal: 10,
            height: "100%",
            justifyContent: "center",
            minWidth: 110,
          },
          containerStyle,
        ]}
      >
        <Calendar
          size={iconSize}
          color={display ? colors.textLight : colors.textFaint}
          strokeWidth={1.5}
        />
        <Text
          className="font-dm-sans"
          style={[
            {
              fontSize: 12,
              color: display ? colors.textLight : colors.textFaint,
            },
            textStyle,
          ]}
        >
          {display ?? placeholder}
        </Text>
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          onPress={() => setOpen(false)}
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.3)",
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation?.()}
            style={{
              width: 320,
              backgroundColor: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              ...Platform.select({
                ios: {
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 24,
                },
                android: { elevation: 12 },
                web: {
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                },
              }),
            }}
          >
            {/* Year selector row */}
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
                paddingHorizontal: 12,
                paddingVertical: 10,
              }}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 4, paddingHorizontal: 1 }}
              >
                {yearRange.map((year) => {
                  const isCurrentYear = year === viewYear;
                  return (
                    <Pressable
                      key={year}
                      onPress={() => {
                        capturePress(pressLabel(String(year)), "pick_year");
                        setViewYear(year);
                      }}
                      style={{
                        borderRadius: 8,
                        paddingHorizontal: 10,
                        paddingVertical: 5,
                        backgroundColor: isCurrentYear
                          ? `${colors.warm}15`
                          : "transparent",
                        borderWidth: isCurrentYear ? 1 : 0,
                        borderColor: isCurrentYear
                          ? `${colors.warm}40`
                          : "transparent",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "600",
                          color: isCurrentYear ? colors.warm : colors.textLight,
                        }}
                      >
                        {year}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>

            {/* Month selector row */}
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: colors.border,
                paddingHorizontal: 12,
                paddingVertical: 8,
              }}
            >
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 4, paddingHorizontal: 1 }}
              >
                {MONTH_ABBREVS.map((month, index) => {
                  const isCurrentMonth = index === viewMonth;
                  return (
                    <Pressable
                      key={month}
                      onPress={() => {
                        capturePress(pressLabel(month), "pick_month");
                        setViewMonth(index);
                      }}
                      style={{
                        borderRadius: 8,
                        paddingHorizontal: 10,
                        paddingVertical: 5,
                        backgroundColor: isCurrentMonth
                          ? `${colors.warm}15`
                          : "transparent",
                        borderWidth: isCurrentMonth ? 1 : 0,
                        borderColor: isCurrentMonth
                          ? `${colors.warm}40`
                          : "transparent",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "600",
                          color: isCurrentMonth
                            ? colors.warm
                            : colors.textLight,
                        }}
                      >
                        {month}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>

            {/* Calendar grid */}
            <View style={{ padding: 12 }}>
              {/* Weekday labels */}
              <View style={{ flexDirection: "row", marginBottom: 4 }}>
                {WEEKDAY_LABELS.map((label) => (
                  <View
                    key={label}
                    style={{
                      flex: 1,
                      alignItems: "center",
                      paddingVertical: 2,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 10,
                        fontWeight: "600",
                        color: colors.textFaint,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Text>
                  </View>
                ))}
              </View>

              {/* Day grid */}
              {Array.from({ length: Math.ceil(days.length / 7) }).map(
                (_, rowIdx) => (
                  <View key={rowIdx} style={{ flexDirection: "row" }}>
                    {days
                      .slice(rowIdx * 7, rowIdx * 7 + 7)
                      .map((day, colIdx) => {
                        if (day === null) {
                          return (
                            <View
                              key={`e-${colIdx}`}
                              style={{ flex: 1, aspectRatio: 1 }}
                            />
                          );
                        }
                        const isSelected =
                          parsed &&
                          day === parsed.getDate() &&
                          viewMonth === parsed.getMonth() &&
                          viewYear === parsed.getFullYear();
                        const isToday =
                          day === today.getDate() &&
                          viewMonth === today.getMonth() &&
                          viewYear === today.getFullYear();

                        return (
                          <View
                            key={day}
                            style={{
                              flex: 1,
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 1,
                            }}
                          >
                            <Pressable
                              onPress={() => {
                                capturePress(
                                  pressLabel(String(day)),
                                  "pick_day",
                                );
                                selectDay(day);
                              }}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: isSelected
                                  ? colors.warm
                                  : "transparent",
                              }}
                            >
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 13,
                                  fontWeight:
                                    isSelected || isToday ? "700" : "400",
                                  color: isSelected
                                    ? "#fff"
                                    : isToday
                                      ? colors.warm
                                      : colors.text,
                                }}
                              >
                                {day}
                              </Text>
                            </Pressable>
                          </View>
                        );
                      })}
                  </View>
                ),
              )}
            </View>

            {/* Today shortcut */}
            <View
              style={{
                borderTopWidth: 1,
                borderTopColor: colors.border,
                paddingVertical: 10,
                paddingHorizontal: 16,
              }}
            >
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Today"), "pick_today");
                  const t = getToday();
                  const mm = String(t.getMonth() + 1).padStart(2, "0");
                  const dd = String(t.getDate()).padStart(2, "0");
                  onChange(`${t.getFullYear()}-${mm}-${dd}`);
                  setOpen(false);
                }}
                style={{ alignItems: "center", paddingVertical: 4 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: colors.warm,
                  }}
                >
                  Today
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
